// ── ข้อมูลเรคอร์ด — one muted line at the bottom of a record drawer ──────────
//
//   #1371519 · สร้าง ราม 16/08/2026 14:22 · แก้ไข แนน 16/08/2026 15:03
//
// Which row this is, who made it, who last touched it. It is what you check when
// a figure looks wrong — read on the way OUT of a drawer, never on the way in —
// so it is ONE line below every field and carries none of their weight. A
// bordered panel of labelled rows gave it the presence of something you are
// meant to fill in, which it is not.
//
// THE SAME LINE ON EVERY DRAWER, and that is the whole point. billing,
// treatment, appointment, lab and ticket rows each name their audit columns
// differently — `Creation_Date` vs `created_at`, `creator` vs `employee_id` vs
// `created_by`, `modified_timestamp` vs `modified_at` — and each drawer had
// grown its own resolver, its own formatter and its own layout, so the same fact
// looked like three different facts. `recordMeta()` absorbs the naming; a new
// drawer needs `<RecordMeta row={x} table="…" />` and nothing else.
//
// ⚠️ NOT an edit history. This is the row's CURRENT state — first hand, last
// hand. The blow-by-blow of who changed what is ประวัติการแก้ไข
// (`/patient/:id/log`), which reads `billing.log[]` and includes voided lines.

import { useEffect, useState } from 'react'
import { Fingerprint } from 'lucide-react'
import { cachedGet } from '../lib/api.js'
import { asList } from '../lib/chart-helpers.js'
import { canAccessStaff } from '../lib/role.js'
import { useMe } from '../lib/me.js'

/* ── timestamps ──────────────────────────────────────────────────────────────
 * Xano timestamp columns are epoch MILLISECONDS; a few feeds hand back an ISO
 * string instead, so both are accepted. Rendered in Asia/Bangkok for the same
 * reason bkkDay() is: the clinic's clock is the only one that counts, and a
 * laptop left on another timezone must not relabel when a row was touched. */
const STAMP = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Bangkok', day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: false,
})

export function tsOf(v) {
  if (v == null || v === '') return 0
  const n = typeof v === 'number' ? v : (/^\d{10,}$/.test(String(v)) ? Number(v) : Date.parse(v))
  return Number.isFinite(n) && n > 0 ? n : 0
}

export const fmtStamp = (v) => { const n = tsOf(v); return n ? STAMP.format(new Date(n)).replace(',', '') : '' }

/* ── who ─────────────────────────────────────────────────────────────────────
 * An actor arrives either as an employee-code addon (`{code}`, or a one-item
 * list of it on some shapes) or as a bare id — `creator` is an OBJECT on the
 * treatment feed and an INT on billing, so the same key must be read both ways
 * and only one of the two can win. */
const asActor = (v) => {
  if (!v || typeof v === 'number') return null
  if (typeof v === 'string') return { code: v }
  if (Array.isArray(v)) return asActor(v[0])
  return (v.code || v.name || v.nickname) ? v : null
}
// `created_by` is an id on most tables but an expanded object — or a plain name
// string — on the ticket feed, so the same key is read as both.
const actorName = (a) => a?.code || a?.name || a?.nickname || ''
const asId = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : 0 }

/** A row from any of the record feeds → the five facts this line states. */
export function recordMeta(row) {
  if (!row || typeof row !== 'object') return null

  const createdAt = row.Creation_Date ?? row.created_at ?? row.created_date ?? null
  const createdId = asId(row.creator) || asId(row.created_by) || asId(row.employee_id) || asId(row.user_id)
  const createdActor = asActor(row._creator) || asActor(row.creator) || asActor(row.created_by) || asActor(row.Creator)

  const modifiedAt = row.modified_timestamp ?? row.modified_at ?? row.Modified_Date ?? null
  const modifiedId = asId(row.modified_by)
  const modifiedActor = asActor(row._modifier) || asActor(row.modifier) || asActor(row.modified_by) || asActor(row.Modifier)

  /* Was this row ever really edited? Writers stamp the INSERT as a modification
     — sampled live 2026-08-16, Xano api 563 and Bubble alike write
     `modified_timestamp` EQUAL to `Creation_Date` to the millisecond, with
     `modified_by` null or 0 — and repeating that as "แก้ไข" on the same line
     reads as an event that never happened. Exact inequality is therefore the
     exact test, and deliberately NOT a tolerance window: a price corrected
     twenty seconds after it was typed is the single edit this line exists to
     show. */
  const ca = tsOf(createdAt)
  const ma = tsOf(modifiedAt)
  const edited = (ma > 0 && ma !== ca) || (modifiedId > 0 && modifiedId !== createdId)

  return { id: row.id ?? null, createdAt, createdId, createdActor, modifiedAt, modifiedId, modifiedActor, edited }
}

/* ── names for the ids the payload did not resolve ───────────────────────────
 * Most feeds hang an employee-code addon on `creator` but NOT on `modified_by`,
 * so without this the edit half reads `#335`. `/api/hr/employees` is Manager ·
 * GM · HR · Admin only, so it is asked for ONLY when the session may read it —
 * everyone else keeps `#335`, which still identifies the person to anyone who
 * can look it up, rather than collecting a 403 every time a drawer opens.
 *
 * Module-scoped as well as cached by `cachedGet`, so the many drawers that mount
 * this share ONE request and a drawer opened later starts already filled. Never
 * surfaced as an error: a missing name costs nothing and this list is not what
 * the drawer is for. */
let sharedCodes = null
export function useEmployeeCodes() {
  const me = useMe()
  const may = canAccessStaff(me)
  const [byId, setById] = useState(sharedCodes)
  useEffect(() => {
    if (!may || sharedCodes) return
    let alive = true
    cachedGet('/api/hr/employees', { per_page: 500 })
      .then((r) => {
        sharedCodes = new Map(asList(r).map((e) => [String(e.id), e]))
        if (alive) setById(sharedCodes)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [may])
  return byId
}

/**
 * @param row    the stored record — null/undefined (a create form) renders nothing
 * @param id     override for rows whose primary key is not `id` (the treatment
 *               feed calls it `treatment_id` on some pages). NOT guessed from
 *               the row: a billing line also carries a `treatment_id`, and
 *               printing that as the record's own id would be a lie.
 * @param table  the Xano table behind it, shown as the id's tooltip
 * @param className  spacing. The default adds no top MARGIN — every drawer body
 *               it closes is a `space-y-*` stack that supplies its own — only the
 *               hairline that separates it from the last field.
 */
export default function RecordMeta({ row, id, table, className = 'pt-3 border-t border-slate-100' }) {
  const codes = useEmployeeCodes()
  const m = recordMeta(row)
  const rowId = id ?? m?.id
  if (!m || rowId == null) return null

  const who = (actor, id) => actorName(actor) || actorName(codes?.get(String(id))) || (id > 0 ? `#${id}` : '—')

  return (
    <p className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-slate-400 ${className}`}>
      <span className="inline-flex items-center gap-1" title={table || undefined}>
        <Fingerprint size={11} />#{rowId}
      </span>
      <span>· สร้าง {who(m.createdActor, m.createdId)} {fmtStamp(m.createdAt)}</span>
      {m.edited && <span>· แก้ไข {who(m.modifiedActor, m.modifiedId)} {fmtStamp(m.modifiedAt)}</span>}
    </p>
  )
}
